import { useEscClose } from '../hooks/useEscClose';
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import {
  X, Trophy, Award, ArrowRight, Sparkles, CheckCircle2,
} from 'lucide-react';

/**
 * ModuleCompleteModal
 * -------------------
 * Big celebration when a learner finishes a module's quiz (score >= 70%).
 *
 * Behaviour
 *  - Fires multi-burst confetti on mount (brand colours).
 *  - Loads the current course + per-module progress to compute "Modul X von Y".
 *  - Three states:
 *      a) "module"      → another module remains  → "Weiter zu Modul X+1" CTA.
 *      b) "course-done" → final module          → 🏆 Kurs geschafft + Zertifikat-CTA.
 *      c) Confetti always; cross-sell always.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Brand confetti palette (cream-50 + coral + peach + terra + sage)
const CONFETTI_COLORS = ['#F58A8A', '#FFA4A4', '#FFC9C9', '#E07856', '#9BB39A', '#FFE5C2'];

const fireConfetti = () => {
  // burst 1 — left
  confetti({
    particleCount: 60,
    angle: 60,
    spread: 70,
    origin: { x: 0, y: 0.65 },
    colors: CONFETTI_COLORS,
    startVelocity: 45,
    scalar: 1.05,
    disableForReducedMotion: true,
  });
  // burst 2 — right
  confetti({
    particleCount: 60,
    angle: 120,
    spread: 70,
    origin: { x: 1, y: 0.65 },
    colors: CONFETTI_COLORS,
    startVelocity: 45,
    scalar: 1.05,
    disableForReducedMotion: true,
  });
  // burst 3 — center, smaller
  setTimeout(() => {
    confetti({
      particleCount: 90,
      spread: 110,
      origin: { y: 0.45 },
      colors: CONFETTI_COLORS,
      ticks: 220,
      startVelocity: 35,
      gravity: 0.9,
      scalar: 0.9,
      disableForReducedMotion: true,
    });
  }, 220);
};

// Curated cross-sell catalogue — mirrors the related-courses block elsewhere in App.js.
const CROSS_SELL_CATALOG = [
  { slug: 'wundmanagement-aufbau',  title: 'Wundmanagement Aufbaumodul',           duration: '60 UE · Zertifikat',           category: 'Wunden' },
  { slug: 'praxisanleitung',        title: 'Weiterbildung Praxisanleitung',        duration: '300 UE · §4 PflAPrV',           category: 'Leitung' },
  { slug: 'pdl-weiterbildung-teil1', title: 'PDL-Weiterbildung Teil 1',            duration: '460 UE · staatl. anerkannt',    category: 'Leitung' },
  { slug: 'palliativpflege',        title: 'Weiterbildung Palliativpflege',        duration: '120 UE · DGP-konform',          category: 'Spezialisierung' },
  { slug: 'hygienebeauftragter',    title: 'Hygienebeauftragter in der Pflege',    duration: '80 UE · 2-jährig',              category: 'Pflicht' },
  { slug: 'gerontopsychiatrie',     title: 'Gerontopsychiatrische Fachweiterbildung', duration: '480 UE · Aufbau',           category: 'Spezialisierung' },
];

const pickCrossSell = (courseId) => {
  const id = (courseId || '').toLowerCase();
  return CROSS_SELL_CATALOG.filter(c => !id.includes(c.slug)).slice(0, 2);
};

const ModuleCompleteModal = ({
  moduleId,
  scorePercentage,
  getAuthHeader,
  onClose,
  onNavigateNext, // (nextModuleId) => void
  onShowCertificate, // () => void   (only used for course-done)
}) => {
  const [loading, setLoading] = useState(true);
  useEscClose(onClose);
  const [thisModule, setThisModule] = useState(null);
  const [courseInfo, setCourseInfo] = useState(null);
  const [moduleList, setModuleList] = useState([]);
  const [progressRows, setProgressRows] = useState([]);

  useEffect(() => {
    fireConfetti();
    // additional smaller burst at 1.4s for sustained delight
    const t = setTimeout(() => {
      confetti({
        particleCount: 40,
        spread: 70,
        origin: { y: 0.5 },
        colors: CONFETTI_COLORS,
        scalar: 0.8,
        disableForReducedMotion: true,
      });
    }, 1400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1) fetch module → get course_id
        const modRes = await axios.get(`${API}/modules/${moduleId}`, getAuthHeader());
        if (cancelled) return;
        setThisModule(modRes.data);

        const courseId = modRes.data?.course_id;
        if (!courseId) { setLoading(false); return; }

        // 2) parallel: course detail + modules list + my progress
        const [courseRes, modulesRes, progRes] = await Promise.all([
          axios.get(`${API}/courses/${courseId}`, getAuthHeader()).catch(() => ({ data: null })),
          axios.get(`${API}/courses/${courseId}/modules`, getAuthHeader()).catch(() => ({ data: [] })),
          axios.get(`${API}/user/progress/${courseId}`, getAuthHeader()).catch(() => ({ data: [] })),
        ]);

        if (cancelled) return;
        setCourseInfo(courseRes.data);
        const mods = (modulesRes.data || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
        setModuleList(mods);
        // The endpoint can return either an array (legacy) or a {progress:[]} envelope
        const rows = Array.isArray(progRes.data) ? progRes.data : (progRes.data?.progress || []);
        setProgressRows(rows);
      } catch (e) {
        // silent — modal still renders with minimal data
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [moduleId, getAuthHeader]);

  const stats = useMemo(() => {
    if (!moduleList.length) return null;

    // A module counts as completed if user_progress.completed OR quiz_passed
    const isDone = (mid) => {
      const row = progressRows.find(p => p.module_id === mid);
      return Boolean(row && (row.completed || row.quiz_passed));
    };

    // Include the just-completed module even if backend hasn't refreshed yet
    const doneCount = moduleList.reduce((c, m) => {
      if (m.id === moduleId) return c + 1; // we just passed it
      return isDone(m.id) ? c + 1 : c;
    }, 0);

    const total = moduleList.length;
    const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;

    // Find next not-yet-done module by order
    const currentOrder = thisModule?.order ?? 999;
    const nextModule = moduleList.find(m => (m.order || 0) > currentOrder && !isDone(m.id))
      || moduleList.find(m => !isDone(m.id) && m.id !== moduleId);

    const isCourseDone = doneCount >= total;
    return { doneCount, total, pct, nextModule, isCourseDone };
  }, [moduleList, progressRows, moduleId, thisModule]);

  const courseId = thisModule?.course_id || '';
  const courseTitle = courseInfo?.title || '';
  const crossSell = useMemo(() => pickCrossSell(courseId), [courseId]);

  // Clean "Modul X: …" prefix from titles for display
  const cleanTitle = (t) => (t || '').replace(/^\s*Modul\s*\d+\s*[:.\-–]\s*/i, '');

  const courseDone = stats?.isCourseDone;
  const next = stats?.nextModule;

  return (
    <div
      className="fixed inset-0 z-[70] bg-espresso/65 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="Kapitel abgeschlossen"
      onClick={onClose}
      data-testid="module-complete-modal"
    >
      <div
        className="bg-cream-50 max-w-xl w-full rounded-3xl shadow-hover overflow-hidden flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HERO */}
        <div
          className={[
            'relative px-7 pt-9 pb-6 overflow-hidden',
            courseDone ? 'bg-espresso text-cream-50' : 'bg-peach-50 text-espresso',
          ].join(' ')}
        >
          {/* Decorative blobs */}
          <div className="pointer-events-none absolute -top-12 -right-10 w-44 h-44 rounded-full bg-coral-100/40 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-10 w-48 h-48 rounded-full bg-peach-100/50 blur-3xl" />

          <button
            onClick={onClose}
            className={`absolute top-3 right-3 p-1.5 rounded-full transition-colors ${
              courseDone ? 'text-cream-200 hover:text-cream-50 hover:bg-cream-50/10' : 'text-sand-500 hover:text-espresso hover:bg-cream-100'
            }`}
            aria-label="Schließen"
            data-testid="module-complete-close"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="relative text-center">
            <div className={`mx-auto mb-3 w-16 h-16 rounded-2xl flex items-center justify-center ${
              courseDone ? 'bg-peach-200/25 text-peach-100' : 'bg-cream-50 text-terra'
            }`}>
              {courseDone ? (
                <Trophy className="h-9 w-9" strokeWidth={1.6} />
              ) : (
                <Sparkles className="h-9 w-9" strokeWidth={1.6} />
              )}
            </div>

            <div className={`text-[11px] uppercase tracking-[0.22em] font-semibold mb-1.5 ${
              courseDone ? 'text-peach-200' : 'text-terra'
            }`}>
              {courseDone ? 'Kurs abgeschlossen' : 'Modul abgeschlossen'}
            </div>

            <h2 className="font-display text-3xl sm:text-4xl leading-tight tracking-wide mb-1">
              {courseDone ? (
                <>🏆 Kurs geschafft!</>
              ) : (
                <>Klasse — Modul {thisModule?.order || ''} ist geschafft!</>
              )}
            </h2>

            {!courseDone && thisModule && (
              <p className={`text-sm sm:text-base font-medium mt-1 ${courseDone ? 'text-cream-200/85' : 'text-sand-700'}`}>
                {`„${cleanTitle(thisModule.title)}"`}
              </p>
            )}

            {typeof scorePercentage === 'number' && (
              <p className={`text-sm mt-2 ${courseDone ? 'text-cream-200/80' : 'text-sand-600'}`}>
                Du hast <strong>{Math.round(scorePercentage)}%</strong> im Quiz erreicht.
              </p>
            )}
          </div>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto px-7 py-5 space-y-5">
          {/* Course progress */}
          {stats && stats.total > 0 && (
            <div className="bg-cream-100 border border-cream-300 rounded-2xl p-4">
              <div className="flex items-baseline justify-between mb-1.5">
                <span className="text-[11px] uppercase tracking-wider text-sand-600 font-semibold">
                  Dein Kursfortschritt
                </span>
                <span className="text-sm font-semibold text-sand-800">
                  {stats.doneCount} <span className="text-sand-500 font-normal">von {stats.total} · {stats.pct}%</span>
                </span>
              </div>
              <div className="h-2.5 rounded-full bg-cream-300 overflow-hidden">
                <div
                  className={`h-full transition-all duration-700 ${
                    stats.isCourseDone ? 'bg-gradient-to-r from-sage to-sage-dark' : 'bg-gradient-to-r from-peach-dark to-terra'
                  }`}
                  style={{ width: `${stats.pct}%` }}
                />
              </div>
              {courseTitle && (
                <p className="text-xs text-sand-500 mt-2 truncate">{courseTitle}</p>
              )}
            </div>
          )}

          {/* Primary CTA */}
          {courseDone ? (
            <button
              onClick={() => { onClose?.(); onShowCertificate?.(); }}
              className="w-full inline-flex items-center justify-center gap-2 bg-espresso hover:bg-terra-dark text-cream-50 rounded-full px-6 py-3.5 font-semibold transition-colors shadow-soft"
              data-testid="module-complete-cta-certificate"
            >
              <Award className="h-5 w-5" />
              Zertifikat öffnen
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : next ? (
            <button
              onClick={() => { onClose?.(); onNavigateNext?.(next.id); }}
              className="w-full inline-flex items-center justify-center gap-2 bg-terra hover:bg-terra-dark text-cream-50 rounded-full px-6 py-3.5 font-semibold transition-colors shadow-soft"
              data-testid="module-complete-cta-next"
            >
              Weiter zu Modul {next.order || ''}
              <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <div className="bg-sage/10 border border-sage/30 rounded-2xl px-5 py-3 text-sand-800 text-sm flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-sage-dark" />
              Du hast alle freigeschalteten Module dieses Kurses geschafft.
            </div>
          )}

          {/* Cross-sell */}
          {!loading && crossSell.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-sand-600 font-bold mb-2 ml-1">
                Das könnte dich auch weiterbringen
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {crossSell.map((c) => (
                  <a
                    key={c.slug}
                    href={`https://dickies-helden.de/#kurse?utm_source=learn&utm_medium=modul-complete&utm_content=${encodeURIComponent(courseId)}&utm_campaign=cross-sell`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-cream-100 border border-cream-300 rounded-2xl p-3.5 hover:shadow-hover hover:border-terra/30 transition-all group"
                    data-testid={`module-complete-xsell-${c.slug}`}
                  >
                    <div className="text-[10px] text-peach-dark uppercase tracking-wider font-semibold mb-1">
                      {c.category}
                    </div>
                    <div className="font-heading font-semibold text-sand-800 text-sm leading-snug mb-1.5 line-clamp-2">
                      {c.title}
                    </div>
                    <div className="text-[11px] text-sand-500 mb-2">{c.duration}</div>
                    <span className="inline-flex items-center gap-1 text-terra text-xs font-semibold group-hover:gap-2 transition-all">
                      Mehr erfahren <ArrowRight className="h-3 w-3" />
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="px-7 py-3 bg-cream-100 border-t border-cream-300 flex items-center justify-end text-xs text-sand-500 flex-shrink-0 gap-3">
          <button
            onClick={() => { fireConfetti(); }}
            className="text-sand-500 hover:text-terra transition-colors"
            data-testid="module-complete-replay-confetti"
            title="Konfetti nochmal"
          >
            🎉 Nochmal feiern
          </button>
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-full bg-sand-800 text-cream-50 hover:bg-espresso transition-colors"
            data-testid="module-complete-dismiss"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModuleCompleteModal;
